import { ActionEntityType } from '@pmp/api';

/** Where a change came from. Origin is a badge on every log entry. */
export enum ChangeOrigin {
  /** A lender user in the portal. Which portal that is depends on the entity, see `getOriginList`. */
  PORTAL = 'PORTAL',
  BROKER_PORTAL = 'BROKER_PORTAL',
  FCA = 'FCA',
}

/** Tag cells take `{ key, value }`: the key selects the styling, the value is the label. */
export type ChangeLogTag = {
  key: string;
  value: string;
};

/** One cell of the log: a list for a field that holds several values, null when it held nothing. */
export type ChangeLogValue = string | string[] | null;

/** One field that a change touched, shown in the expanded detail of its row. */
export type ChangeLogFieldChange = {
  id: string;
  /** The change request grid's column key. Dotted for a field of a collection, as `tradingAddresses.postCode`. */
  field: string;
  /** The row of a collection this field belongs to, absent for the record's own fields. */
  item?: string | null;
  previousValue: ChangeLogValue;
  newValue: ChangeLogValue;
};

/** What one collection inside the record gained, lost or changed. */
export type ChangeLogCollectionChange = {
  /** The field the collection travels in, e.g. `tradingAddresses`. */
  field: string;
  changes: ChangeLogFieldChange[];
};

/** One change: everything a single author applied at one moment, over one or more fields. */
export type ChangeLogEntry = {
  id: string;
  changedBy: string;
  changedAt: string | null;
  origin: ChangeLogTag;
  /** Set only for changes that came through a change request, so Portal changes only. */
  changeRequestId: string | null;
  /** Column keys of the fields this change touched. Drives the Fields column and its filter. */
  fields: string[];
  /** The change request's entity type, which in a base rate's log can be an interest rate's. */
  entityType: string | null;
};

/** What one change did to the record, loaded when its row is expanded. */
export type ChangeLogDetail = {
  /** The record's own fields. A collection's rows are in `collections`, which renders as its own table. */
  changes: ChangeLogFieldChange[];
  collections: ChangeLogCollectionChange[];
  /** Set when the change could not be read. */
  failed?: boolean;
};

export type ChangeLogTarget = {
  entityType:
    | ActionEntityType.BROKERS
    | ActionEntityType.FIRMS
    | ActionEntityType.PRODUCTS
    | ActionEntityType.PROCURATION_FEES
    | ActionEntityType.PROCURATION_FEE_RULES
    | ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS
    | ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS
    | ActionEntityType.BASE_RATES
    | ActionEntityType.EXCHANGE_RATES
    | ActionEntityType.FEES
    | ActionEntityType.FEE_PROFILES
    | ActionEntityType.ERC_PROFILES
    | ActionEntityType.EXCLUSIVITY_SETS;
  id: string;
  name: string;
};

export type ChangeLogState = {
  open: boolean;
  target: ChangeLogTarget | null;
};
